import EventEmitter from "@folio-2026/shared/eventemitter"
import * as THREE from "three"

export type AudioSource = {
  name: string
  path: string
}

export type AudioSources = AudioSource[]

export type AudioLoadResult = AudioBuffer

export default class AudioLoader extends EventEmitter {
  items: { [key: string]: AudioLoadResult }
  sources: AudioSources
  loader: THREE.AudioLoader
  manager: THREE.LoadingManager
  readyTriggered: boolean = false

  constructor(sources: AudioSources) {
    super()

    // Options
    this.sources = sources
    this.items = {}

    this.manager = new THREE.LoadingManager(
      this.onResourceLoad.bind(this),
      this.onResourceProgress.bind(this),
    )

    this.loader = new THREE.AudioLoader(this.manager)

    // Start loading
    this.startLoading()
  }

  /**
   * This is called by LoadingManager when all resources finish loading
   */
  onResourceLoad() {
    this.checkAndTriggerReady()
  }

  /**
   * Check if all resources are loaded and trigger ready event
   */
  checkAndTriggerReady() {
    // Prevent multiple triggers
    if (this.readyTriggered) {
      return
    }

    // Check if all sources are loaded
    const allLoaded = this.sources.every((source) => source.name in this.items)

    if (allLoaded) {
      this.readyTriggered = true
      this.trigger("ready")
    }
  }

  onResourceProgress(url: string, loaded: number, total: number) {
    this.trigger("progress", [url, loaded, total])
  }

  /**
   * Create a THREE.Audio instance from a loaded audio buffer
   * @param name - The name of the audio source
   * @param listener - The AudioListener instance (required for Audio)
   * @returns A THREE.Audio instance, or null if the audio buffer is not found
   */
  createAudio(name: string, listener: THREE.AudioListener): THREE.Audio | null {
    const buffer = this.items[name]
    if (!buffer) {
      console.warn(`Audio buffer "${name}" not found`)
      return null
    }

    const audio = new THREE.Audio(listener)
    audio.setBuffer(buffer)
    return audio
  }

  /**
   * Get an audio buffer by name
   * @param name - The name of the audio source
   * @returns The AudioBuffer, or undefined if not found
   */
  getAudioBuffer(name: string): AudioLoadResult | undefined {
    return this.items[name]
  }

  startLoading() {
    if (this.sources.length === 0) {
      requestAnimationFrame(() => {
        this.onResourceLoad()
      })
      return
    }

    for (const source of this.sources) {
      this.loader.load(
        source.path,
        (buffer) => {
          this.items[source.name] = buffer
          // Check if all resources are loaded
          this.checkAndTriggerReady()
        },
        () => null,
        (error) => {
          console.error(`Error loading audio "${source.name}":`, error)
          // Still check if we should trigger ready (in case of errors)
          this.checkAndTriggerReady()
        },
      )
    }
  }
}
